import type { RecetaAPI, IngredienteRecetaAPI } from '../services/recetasAPI';
import type { Receta, IngredienteReceta, EstadoReceta } from '../types/recetas';

// Normaliza cualquier texto de unidad ('gramos', 'mililitros', etc.) a su
// abreviación corta ('g', 'ml', ...). Si viene ya un símbolo ('g'), lo devuelve
// tal cual. Si no se puede clasificar, devuelve el string original.
function normalizarAbreviacion(u: unknown): string {
  if (!u) return '';
  const s = String(u).trim().toLowerCase();
  if (!s) return '';
  if (['gramos', 'gramo', 'g'].includes(s)) return 'g';
  if (['kilogramos', 'kilogramo', 'kilo', 'kilos', 'kg'].includes(s)) return 'kg';
  if (['mililitros', 'mililitro', 'ml'].includes(s)) return 'ml';
  if (['litros', 'litro', 'lt', 'lts', 'l'].includes(s)) return 'l';
  if (['unidades', 'unidad', 'u', 'ud', 'c/u', 'cu'].includes(s)) return 'ud';
  if (['cucharadas', 'cucharada', 'cda', 'cdas'].includes(s)) return 'cda';
  if (['cucharaditas', 'cucharadita', 'cdita', 'cditas'].includes(s)) return 'cdita';
  if (['tazas', 'taza', 'tz', 'tzs'].includes(s)) return 'taza';
  if (['pizca', 'pizcas'].includes(s)) return 'pizca';
  return s;
}

// Función para mapear estados de la API al frontend
function mapearEstadoAPI(estadoAPI: string | undefined | null): EstadoReceta {
  if (!estadoAPI) {
    console.warn(
      `Estado desconocido de la API: "${estadoAPI}", usando "borrador" como fallback`
    );
    return 'borrador';
  }

  switch (estadoAPI) {
    case 'borrador':
      return 'borrador';
    case 'aprobada':
    case 'disponible':
      return 'disponible';
    case 'archivada':
    case 'inactiva':
      return 'archivada';
    default:
      console.warn(
        `Estado desconocido de la API: "${estadoAPI}", usando "borrador" como fallback`
      );
      return 'borrador';
  }
}

// Adaptador para convertir RecetaAPI a Receta (formato frontend)
export function adaptarRecetaAPIToReceta(recetaAPI: RecetaAPI): Receta {
  // Debug: Log para verificar estructura de receta recibida
  console.log('🔍 Adaptador - Receta recibida de API:', {
    nombre: recetaAPI.nombre,
    ingredientes_receta_count: (recetaAPI.ingredientes_receta || []).length,
    ingredientes_count: (recetaAPI.ingredientes || []).length,
    tiene_ingredientes_receta: !!recetaAPI.ingredientes_receta,
    tiene_ingredientes: !!recetaAPI.ingredientes,
  });

  // Primero mapear los ingredientes para tener acceso a sus costos
  const ingredientes =
    recetaAPI.ingredientes_receta || recetaAPI.ingredientes || [];

  console.log(
    '🔍 Adaptador - Ingredientes encontrados:',
    ingredientes.length,
    ingredientes
  );

  const ingredientesMapeados = ingredientes.map(
    adaptarIngredienteRecetaAPIToIngredienteReceta
  );

  // Calcular el costo total basado en los ingredientes
  const costoTotalCalculado = ingredientesMapeados.reduce(
    (total, ingrediente) => {
      return total + (ingrediente.costoLinea || 0);
    },
    0
  );

  // Resolver tipo de plato. El backend serializa la columna como `tipo_plato`
  // (snake_case). Mantenemos también los fallbacks legacy por si alguna
  // respuesta llega con un shape distinto (relación tipos_plato, camelCase, etc.).
  // Default '' (vacío) — si la receta no tiene tipo en BD, el modal de
  // edición abre con el <option disabled> "Seleccioná un tipo" y el
  // required del select bloquea el submit. Antes era 'Normal', un valor
  // que NO existe en ningún catálogo y que viajaba al backend → 400
  // silencioso (ver salida 034).
  let tipoComidaFinal: string = '';

  // Caso principal: campo `tipo_plato` directo (string) — fuente de verdad.
  const tipoPlatoRaw = (recetaAPI as any).tipo_plato;
  if (tipoPlatoRaw) {
    tipoComidaFinal =
      typeof tipoPlatoRaw === 'object' && tipoPlatoRaw.nombre
        ? tipoPlatoRaw.nombre
        : String(tipoPlatoRaw);
  }
  // Compatibilidad: relación `tipos_plato` (objeto con nombre)
  else if ((recetaAPI as any).tipos_plato?.nombre) {
    tipoComidaFinal = (recetaAPI as any).tipos_plato.nombre;
  }
  // Compatibilidad: campo `tipoPlato` camelCase
  else if ((recetaAPI as any).tipoPlato) {
    const tipoPlato = (recetaAPI as any).tipoPlato;
    if (typeof tipoPlato === 'object' && tipoPlato.nombre) {
      tipoComidaFinal = tipoPlato.nombre;
    } else if (typeof tipoPlato === 'string') {
      tipoComidaFinal = tipoPlato;
    }
  }
  // Fallback legacy a tipo_comida
  else if (recetaAPI.tipo_comida) {
    tipoComidaFinal = recetaAPI.tipo_comida;
  }

  return {
    id: recetaAPI.id.toString(),
    nombre: recetaAPI.nombre,
    descripcion: recetaAPI.descripcion || '',
    tipoPlato: tipoComidaFinal,
    porciones: recetaAPI.porciones,
    estado: mapearEstadoAPI(recetaAPI.estado),
    notasParaCocina: recetaAPI.instrucciones || '',
    costoTotal: costoTotalCalculado || Number(recetaAPI.costo_total || 0),
    tags: recetaAPI.tags || [],
    ingredientes: ingredientesMapeados,
    fechaCreacion: new Date(recetaAPI.created_at),
    fechaActualizacion: new Date(recetaAPI.updated_at),
    usuarioCreadorId: recetaAPI.chef_id?.toString() || '1',
    usuarioCreador:
      recetaAPI.usuarios || recetaAPI.chef
        ? {
            id: (
              recetaAPI.usuarios?.id ||
              recetaAPI.chef?.id ||
              recetaAPI.chef_id ||
              1
            ).toString(),
            nombre:
              recetaAPI.usuarios?.nombre ||
              recetaAPI.chef?.nombre ||
              'Usuario Desconocido',
            email: recetaAPI.usuarios?.email || recetaAPI.chef?.email || '',
          }
        : {
            id: recetaAPI.chef_id?.toString() || '1',
            nombre: 'Usuario Desconocido',
            email: '',
          },
  };
}

// Adaptador para convertir IngredienteRecetaAPI a IngredienteReceta
export function adaptarIngredienteRecetaAPIToIngredienteReceta(
  ingredienteAPI: IngredienteRecetaAPI
): IngredienteReceta {
  // Debug: Log para verificar estructura recibida
  console.log('🔍 Adaptador - Ingrediente recibido de API:', {
    id: ingredienteAPI.id,
    ingrediente_singular: ingredienteAPI.ingrediente ? 'existe' : 'no existe',
    ingredientes_plural: ingredienteAPI.ingredientes ? 'existe' : 'no existe',
    raw: ingredienteAPI,
  });
  // El backend envía 'ingredientes' (plural)
  const ingredienteData =
    (ingredienteAPI as any).ingredientes || ingredienteAPI.ingrediente;

  if (!ingredienteData) {
    console.error(
      'No se encontró información del ingrediente en:',
      ingredienteAPI
    );
    throw new Error(
      'Información de ingrediente faltante en la respuesta de la API'
    );
  }

  return {
    id: ingredienteAPI.id.toString(),
    recetaId: ingredienteAPI.receta_id.toString(),
    ingredienteId: ingredienteAPI.ingrediente_id.toString(),
    ingrediente: {
      id: ingredienteData.id.toString(),
      nombre: ingredienteData.nombre,
      descripcion: ingredienteData.descripcion || '',
      categoria: ingredienteData.categoria || 'Sin categoría',
      unidadMedidaId: (ingredienteData.unidad_display_id ??
                        ingredienteData.unidad_base_id ??
                        '1').toString(),
      unidadMedida: {
        id: (ingredienteData.unidad_display_id ??
             ingredienteData.unidad_base_id ??
             '1').toString(),
        nombre: ingredienteData.unidad_medida || 'unidad',
        abreviacion: normalizarAbreviacion(
          ingredienteData.unidad_display_simbolo ||
          ingredienteData.unidad_base_simbolo ||
          ingredienteData.unidad_medida
        ) || 'ud',
        tipo: ingredienteData.tipo_fisico || 'peso',
        activa: true,
      },
      stockActual: Number(ingredienteData.stock_actual || 0),
      stockMinimo: Number(ingredienteData.stock_minimo || 0),
      stockReservado: 0, // No disponible en API
      diasAlertaVencimiento: 7, // Default
      activo:
        ingredienteData.activo !== undefined ? ingredienteData.activo : true,
      fechaCreacion: new Date(ingredienteData.created_at || Date.now()),
      fechaActualizacion: new Date(ingredienteData.updated_at || Date.now()),
      proveedores: [], // No disponible en API
      costoUnitario: Number(ingredienteData.costo_unitario || 0), // Agregar costo unitario desde la API
    },
    cantidad: Number(ingredienteAPI.cantidad || 0),
    unidadMedidaId: ((ingredienteAPI as any).unidad_medida_id ??
                     ingredienteData.unidad_display_id ??
                     ingredienteData.unidad_base_id ??
                     '1').toString(),
    // getUnidadMedidaText soporta tanto string como objeto; armamos objeto con
    // abreviación normalizada para que la UI muestre 'g'/'ml' en vez de 'kg'.
    unidadMedida: {
      id: ((ingredienteAPI as any).unidad_medida_id ??
           ingredienteData.unidad_display_id ??
           ingredienteData.unidad_base_id ??
           '1').toString(),
      nombre: (ingredienteAPI.unidad ||
               (ingredienteAPI as any).unidad_medida ||
               ingredienteData.unidad_medida ||
               'unidad') as string,
      abreviacion: normalizarAbreviacion(
        ingredienteAPI.unidad ||
        (ingredienteAPI as any).unidad_medida ||
        ingredienteData.unidad_display_simbolo ||
        ingredienteData.unidad_base_simbolo ||
        ingredienteData.unidad_medida
      ) || 'ud',
      tipo: ingredienteData.tipo_fisico || 'peso',
      activa: true,
    },
    notas: '',
    costoLinea:
      Number(ingredienteAPI.costo_parcial || 0) ||
      Number(ingredienteAPI.cantidad || 0) *
        Number(ingredienteData.costo_unitario || 0), // Calcular como respaldo si no viene desde la API
    // Calcular información de stock
    stockDisponible: Number(ingredienteData.stock_actual || 0),
    stockSuficiente:
      Number(ingredienteData.stock_actual || 0) >=
      Number(ingredienteAPI.cantidad || 0),
  };
}

// Función para mapear estados del frontend a la API
function mapearEstadoFrontendToAPI(estadoFrontend: EstadoReceta): string {
  switch (estadoFrontend) {
    case 'borrador':
      return 'borrador';
    case 'disponible':
      return 'aprobada'; // El backend usa "aprobada" en lugar de "disponible"
    case 'archivada':
      return 'archivada'; // Asumimos que el backend acepta "archivada"
    default:
      return 'borrador';
  }
}

// Adaptador para convertir Receta a datos para API
export function adaptarRecetaToAPI(receta: Partial<Receta>) {
  return {
    nombre: receta.nombre || '',
    descripcion: receta.descripcion || '',
    tipo_comida: receta.tipoPlato || 'Normal',
    porciones: receta.porciones || 1,
    tiempo_preparacion: 30, // Default temporalmente
    instrucciones: receta.notasParaCocina || '',
    estado: receta.estado
      ? mapearEstadoFrontendToAPI(receta.estado)
      : 'borrador',
  };
}
